import {
  Appointment,
  CommunicationMetaItem,
  FOLLOWUP_MESSAGE_PURPOSE,
} from "@elixir/types";
import dateFormat from "dateformat";
import { ReactComponent as BanRegularIcon } from "../assets/icons/font-awesome/ban-regular.svg";
import { ReactComponent as BellSolidIcon } from "../assets/icons/font-awesome/bell-solid.svg";
import { ReactComponent as CalendarDayRegularIcon } from "../assets/icons/font-awesome/calendar-day-regular.svg";
import { ReactComponent as CalendarPenRegularIcon } from "../assets/icons/font-awesome/calendar-pen-regular.svg";
import { ReactComponent as CircleCheckSolidIcon } from "../assets/icons/font-awesome/circle-check.svg";
import { ReactComponent as ClockRegularIcon } from "../assets/icons/font-awesome/clock-regular.svg";
import { ReactComponent as EllipsisRegularIcon } from "../assets/icons/font-awesome/ellipsis-regular.svg";
import { ReactComponent as HourglassHalfRegularIcon } from "../assets/icons/font-awesome/hourglass-half-regular.svg";
import { ReactComponent as PersonWalkingDashedArrowRightRegularIcon } from "../assets/icons/font-awesome/person-walking-dashed-arrow-right-regular.svg";
import { ReactComponent as XmarkRegularIcon } from "../assets/icons/font-awesome/xmark-regular.svg";
import {
  QUEUE_TYPE,
  aptCancelledStatuses,
  aptNoShowStatuses,
  rescheduledStatuses,
} from "../constants/appointments";
import { CalendarRegularIcon } from "@elixir/icons";
import { getAptSlotPrefFromTime } from "./queue-utils";

export type QueueType =
  | "checkin"
  | "completed"
  | "booked"
  | "followup"
  | "others"
  | "billing"
  | "requested";

const successStatuses = ["sent", "delivered", "clicked", "read"];

export function getAppointmentCardProps(
  appointment: Appointment,
  queue: QueueType
) {
  let statusPanelChips: StatusPanelChip[] = [];

  const statusCard = getStatusCard(appointment, queue);

  if (statusCard) {
    statusPanelChips.push(statusCard);
    return { statusPanelChips };
  }

  const reminderStatusCard = getReminderStatus(appointment, queue);

  if (reminderStatusCard) {
    statusPanelChips = [reminderStatusCard, ...statusPanelChips];
  }

  return { statusPanelChips };
}

export type StatusPanelChip = {
  backgroundColor: string;
  text: string;
  textColor: string;
  StyledIcon: JSX.Element;
};

export function getReminderStatus(
  appointment: Appointment,
  queue: QueueType
): StatusPanelChip | undefined {
  const { comm_meta = [] } = appointment;
  let communicationMetaToDisplay: CommunicationMetaItem | null = null;

  if (comm_meta && comm_meta.length && queue !== "others") {
    const communicationsToConsider = comm_meta.filter(
      ({ status: commStatus, purpose }) =>
        commStatus !== "Cancelled" && queue === QUEUE_TYPE.FOLLOWUP
          ? purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_REMINDER
          : purpose === FOLLOWUP_MESSAGE_PURPOSE.APPOINTMENT_REMINDER
    );

    communicationsToConsider.sort((a, b) => {
      const currTime = new Date();
      const diffOfAFromCurrTime = Math.abs(
        currTime.getTime() - new Date((a.sentAt || a.schFor)!).getTime()
      );

      const diffOfBFromCurrTime = Math.abs(
        currTime.getTime() - new Date((b.sentAt || b.schFor)!).getTime()
      );

      return diffOfAFromCurrTime - diffOfBFromCurrTime;
    });

    communicationMetaToDisplay = communicationsToConsider[0];
  }

  if (communicationMetaToDisplay) {
    const { status: commStatus, sentAt, schFor } = communicationMetaToDisplay;
    const timeToSend = new Date((sentAt || schFor)!).getTime();
    const now = new Date().getTime();

    const totalMinutes = Math.floor(Math.abs(timeToSend - now) / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);

    if (commStatus === "Scheduled") {
      return {
        backgroundColor: "dr-bg-doctor-darwin-accent-symptoms-blue-200",
        text: `Reminder at ${dateFormat(timeToSend, "dd mmm | hh:MM tt")}`,
        textColor: "dr-text-doctor-darwin-accent-symptoms-blue-900",
        StyledIcon: <ClockRegularIcon />,
      };
    } else if (successStatuses.includes(commStatus?.toLowerCase())) {
      return {
        backgroundColor: "dr-bg-darwin-touch-green-bg-light",
        textColor: "dr-text-darwin-touch-green-primary",
        text: `Sent ${
          totalHours === 0 ? `${totalMinutes}m` : `${totalHours}h`
        } ago`,
        StyledIcon: <BellSolidIcon />,
      };
    }
  } else if (queue === QUEUE_TYPE.FOLLOWUP) {
    const appointmentDate = new Date(appointment.date);
    const messagingDate = new Date(appointmentDate);
    messagingDate.setDate(appointmentDate.getDate() - 1);
    const now = new Date();

    if (messagingDate.getTime() < now.getTime() + 5 * 60 * 1000) {
      messagingDate.setDate(appointmentDate.getDate());
    }

    if (messagingDate.getTime() > now.getTime() - 5 * 60 * 1000) {
      return {
        backgroundColor: "dr-bg-doctor-darwin-accent-symptoms-blue-200",
        text: `Reminder at ${dateFormat(messagingDate, "dd mmm")} | 08:30 am`,
        textColor: "dr-text-doctor-darwin-accent-symptoms-blue-900",
        StyledIcon: <ClockRegularIcon />,
      };
    }
  }
}

export function getStatusCard(appointment: Appointment, queue: QueueType) {
  const { status, date, start_time, comm_meta } = appointment;

  if (queue === "booked") {
    const sentMessageStatusWhitelist = new Set<CommunicationMetaItem["status"]>(
      ["Delivered", "Responded", "Read", "Sent", "Clicked"]
    );

    const reminderMessage = appointment.comm_meta?.find(
      (item) =>
        item.purpose === FOLLOWUP_MESSAGE_PURPOSE.APPOINTMENT_REMINDER &&
        sentMessageStatusWhitelist.has(item.status)
    );

    if (reminderMessage) {
      const reminderMessageResponse =
        reminderMessage.messageResponse?.toLowerCase();

      if (reminderMessageResponse && reminderMessageResponse.includes("yes")) {
        return getConfirmedVisitStripProps({});
      } else if (
        reminderMessageResponse &&
        reminderMessageResponse.includes("no")
      ) {
        const notComingResponseMessage = appointment.comm_meta?.find(
          (item) =>
            item.purpose ===
            FOLLOWUP_MESSAGE_PURPOSE.APPOINTMENT_REMINDER_NO_RESPONSE
        )?.messageResponse;

        return getNotComingStripProps({
          additionalMessage: notComingResponseMessage,
        });
      } else if (
        reminderMessageResponse &&
        reminderMessageResponse.includes("later")
      ) {
        return getNotComingStripProps({
          additionalMessage: reminderMessageResponse,
        });
      }

      return getAwaitingResponseStripProps();
    }
  } else if (queue === QUEUE_TYPE.FOLLOWUP) {
    const isReminderMessageSent =
      !!comm_meta?.length &&
      !!comm_meta.filter(
        (item) => item.purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_REMINDER
      ).length;

    if (isReminderMessageSent) {
      if (status === "IN") {
        //awaiting response yet.
        return getAwaitingResponseStripProps();
      } else if (status === "RV") {
        const communicationSentForImComing = comm_meta?.find(
          (comm) =>
            comm.purpose ===
            FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_ACCEPTANCE_RESPONSE
        );

        const responseMessage = communicationSentForImComing?.messageResponse
          ? communicationSentForImComing.messageResponse
          : "";

        let formattedResponseMessage = "";
        if (responseMessage) {
          const [startTime, endTime, _] = responseMessage
            .replace(/\s/g, "")
            .split("-");
          formattedResponseMessage = `Prefer to visit at ${startTime} - ${endTime}`;
        }

        return getConfirmedVisitStripProps({
          additionalMessage: formattedResponseMessage,
        });
      } else if (status === "BK") {
        return {
          text: dateFormat(start_time || date, "dd mmm | hh:MM TT"),
          backgroundColor: "dr-bg-darwin-touch-green-bg-light",
          textColor: "dr-text-darwin-touch-green-primary",
          StyledIcon: <CalendarDayRegularIcon />,
        };
      } else if (status === "PC") {
        const responseMessagingForNotComing =
          comm_meta?.find(
            (comm) =>
              comm.purpose ===
              FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_REJECTION_RESPONSE
          ) ||
          comm_meta?.find(
            (comm) =>
              comm.purpose === FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_REMINDER
          );

        return getNotComingStripProps({
          additionalMessage: responseMessagingForNotComing?.messageResponse,
        });
      } else if (status === "RE") {
        const communicationForIllComeLater = comm_meta?.find(
          (comm) =>
            comm.purpose ===
            FOLLOWUP_MESSAGE_PURPOSE.FOLLOWUP_RESCHEDULE_RESPONSE
        );
        const responseMessage =
          communicationForIllComeLater?.messageResponse ?? "";
        return getRescheduledStripProps({
          additionalMessage: responseMessage,
        });
      }
    }
  } else if (queue === QUEUE_TYPE.REQUESTED) {
    let dayOfApt = dateFormat(appointment.start_time, "DDDD");
    const slot_pref = getAptSlotPrefFromTime(new Date(appointment.start_time));
    const comm_meta = appointment.comm_meta;
    if (
      comm_meta?.[0]?.status === "Responded" &&
      comm_meta?.[0]?.messageResponse === "flexible"
    ) {
      dayOfApt = "Any Day";
    }
    return getRequestedVisitStripProps({ day: dayOfApt, slot_pref });
  }

  if (status === "CK") {
    return getCheckedInStripProps();
  } else if (status === "OG") {
    return getOngoingStripProps();
  } else if (status === "CM" || status === "CMNP") {
    //completed mark
    return getCompletedVisitStripProps();
  } else if (aptNoShowStatuses.includes(status)) {
    return {
      text: "Marked No Show",
      textColor: "dr-text-darwin-touch-red-primary",
      backgroundColor: "dr-bg-darwin-touch-red-bg-light",
      StyledIcon: <BanRegularIcon />,
    };
  } else if (aptCancelledStatuses.includes(status)) {
    return {
      text: "Cancelled",
      textColor: "dr-text-darwin-touch-red-primary",
      backgroundColor: "dr-bg-darwin-touch-red-bg-light",
      StyledIcon: <XmarkRegularIcon />,
    };
  } else if (rescheduledStatuses.includes(status)) {
    return getRescheduledStripProps({});
  }
}

function getRescheduledStripProps({
  additionalMessage,
}: {
  additionalMessage?: string;
}) {
  return {
    text: `Rescheduled${additionalMessage ? ` - ${additionalMessage}` : ""}`,
    textColor: "dr-text-darwin-touch-red-primary",
    backgroundColor: "dr-bg-darwin-touch-red-bg-light",
    StyledIcon: <CalendarPenRegularIcon />,
  };
}

function getAwaitingResponseStripProps() {
  return {
    text: "Awaiting Response",
    backgroundColor: "dr-bg-darwin-touch-yellow-bg-light",
    textColor: "dr-text-darwin-touch-yellow-primary",
    StyledIcon: <HourglassHalfRegularIcon />,
  };
}

function getConfirmedVisitStripProps({
  additionalMessage,
}: {
  additionalMessage?: string;
}) {
  return {
    text: `Confirmed Visit${
      additionalMessage ? ` - ${additionalMessage}` : ""
    }`,
    textColor: "dr-text-darwin-touch-green-primary",
    backgroundColor: "dr-bg-darwin-touch-green-bg-light",
    StyledIcon: <CircleCheckSolidIcon />,
  };
}

function getCheckedInStripProps() {
  return {
    text: "Checked In",
    backgroundColor: "dr-bg-darwin-touch-green-bg-light",
    textColor: "dr-text-darwin-touch-green-primary",
    StyledIcon: <PersonWalkingDashedArrowRightRegularIcon />,
  };
}

function getOngoingStripProps() {
  return {
    text: "Ongoing",
    textColor: "dr-text-darwin-touch-yellow-primary",
    backgroundColor: "dr-bg-darwin-touch-yellow-bg-light",
    StyledIcon: <EllipsisRegularIcon />,
  };
}

function getNotComingStripProps({
  additionalMessage,
}: {
  additionalMessage?: string;
}) {
  return {
    text: `Not coming${additionalMessage ? ` - ${additionalMessage}` : ""}`,
    textColor: "dr-text-darwin-touch-red-primary",
    backgroundColor: "dr-bg-darwin-touch-red-bg-light",
    StyledIcon: <BanRegularIcon />,
  };
}

function getCompletedVisitStripProps() {
  return {
    text: "Completed Visit",
    textColor: "dr-text-darwin-touch-green-primary",
    backgroundColor: "dr-bg-darwin-touch-green-bg-light",
    StyledIcon: <CircleCheckSolidIcon />,
  };
}

function getRequestedVisitStripProps({
  day,
  slot_pref,
}: {
  day: string;
  slot_pref: "Morning" | "Afternoon" | "Evening";
}) {
  return {
    text: `Patient preference: ${day} | ${slot_pref}`,
    textColor: "dr-text-darwin-touch-neutral-600",
    backgroundColor:
      "dr-bg-darwin-touch-neutral-0 dr-border-1 dr-border-darwin-touch-neutral-100",
    StyledIcon: <CalendarRegularIcon />,
  };
}
